// Step 3: Classification - Collect classification data about the client's legal issue

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useCreateVisit, useCreateVisitClassification } from '../../../../hooks/useVisits'
import { Button } from '../../../../components/ui/button'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { Checkbox } from '../../../../components/ui/checkbox'
import { Separator } from '../../../../components/ui/separator'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card'
import {
  GENERAL_CLASSIFICATIONS,
  PWD_CLASSIFICATIONS,
  OTHER_CLASSIFICATIONS,
  type ClientClassification,
} from '../../../../types/visit-wizard'
import { calculateAge } from '../../../../lib/utils'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { mapClassificationsToTypeIds } from '../../../../constants/classification-types'
import { useAuth } from '../../../../contexts/AuthContext'

// Validation schema
const classificationSchema = z.object({
  client_classifications: z.array(z.string()).default([]),
})

type ClassificationFormData = z.infer<typeof classificationSchema>

export function Classification() {
  const { state, setClassification, goBack, setVisitId } = useVisitWizard()
  const { user } = useAuth()
  const client = state.selectedClient!
  const age = calculateAge(client.birthday)
  const createVisit = useCreateVisit()
  const createClassification = useCreateVisitClassification()

  const [showAssistDialog, setShowAssistDialog] = useState(false)
  const [showDenyDialog, setShowDenyDialog] = useState(false)
  const [isCreatingVisit, setIsCreatingVisit] = useState(false)

  const form = useForm<ClassificationFormData>({
    resolver: zodResolver(classificationSchema),
    defaultValues: {
      client_classifications: state.classification?.client_classifications || [],
    },
  })

  const handleAssist = async () => {
    const data = form.getValues()
    const classificationData = {
      client_classifications: data.client_classifications as ClientClassification[],
      decision: 'assist' as const,
    }

    setIsCreatingVisit(true)
    setShowAssistDialog(false)

    try {
      // Prepare client_info snapshot (matches legacy database structure)
      // All fields must use "" for null/empty values
      const clientInfo = {
        firstname: client.firstname || '',
        middlename: client.middlename || '',
        lastname: client.lastname || '',

        birthday: client.birthday || '',
        gender: client.gender || '',
        civil_status: client.civil_status || '',
        contact_number: client.contact_number || '',
        email: client.email || '',
        street_name: client.street_name || '',
        barangay: client.barangay || '',
        city: client.city || '',
        province: client.province || '',
        citizenship: client.citizenship || '',
        monthly_income: client.monthly_income || 0,
      }

      // Convert classification labels to type IDs
      const classificationTypeIds = mapClassificationsToTypeIds(
        data.client_classifications as ClientClassification[]
      )

      // Create visit in backend
      const visit = await createVisit.mutateAsync({
        client_id: client.id,
        office_id: user?.office_id ? parseInt(user.office_id) : 1, // Use authenticated user's office or default to 1
        user_id: user?.id, // Authenticated user ID
        client_info: clientInfo, // Snapshot of client data at visit time
        status: 100, // 100 = ASSISTED (matches legacy)
      })

      // Store visit ID
      setVisitId(visit.id)

      // Save classification to backend with type IDs array
      await createClassification.mutateAsync({
        visit_id: visit.id,
        classification_id: 100, // 100 = ASSISTED classification
        detail: { types: classificationTypeIds }, // Array of type IDs, not labels
      })

      // Set classification and navigate
      setClassification(classificationData)

      toast.success('Visit created successfully')
    } catch (error: any) {
      console.error('Failed to create visit:', error)
      toast.error(error.message || 'Failed to create visit. Please try again.')
      setIsCreatingVisit(false)
    }
  }

  const handleDeny = async () => {
    const data = form.getValues()
    const classificationData = {
      client_classifications: data.client_classifications as ClientClassification[],
      decision: 'deny' as const,
    }

    setIsCreatingVisit(true)
    setShowDenyDialog(false)

    try {
      // Prepare client_info snapshot (matches legacy database structure)
      // All fields must use "" for null/empty values
      const clientInfo = {
        firstname: client.firstname || '',
        middlename: client.middlename || '',
        lastname: client.lastname || '',

        birthday: client.birthday || '',
        gender: client.gender || '',
        civil_status: client.civil_status || '',
        contact_number: client.contact_number || '',
        email: client.email || '',
        street_name: client.street_name || '',
        barangay: client.barangay || '',
        city: client.city || '',
        province: client.province || '',
        citizenship: client.citizenship || '',
        monthly_income: client.monthly_income || 0,
      }

      // Convert classification labels to type IDs
      const classificationTypeIds = mapClassificationsToTypeIds(
        data.client_classifications as ClientClassification[]
      )

      // Create visit in backend first (so we have a visit ID for the denial)
      const visit = await createVisit.mutateAsync({
        client_id: client.id,
        office_id: user?.office_id ? parseInt(user.office_id) : 1, // Use authenticated user's office or default to 1
        user_id: user?.id, // Authenticated user ID
        client_info: clientInfo, // Snapshot of client data at visit time
        status: 101, // 101 = DENIED (matches legacy)
      })

      // Store visit ID
      setVisitId(visit.id)

      // Save classification to backend with type IDs array
      await createClassification.mutateAsync({
        visit_id: visit.id,
        classification_id: 101, // 101 = DENIED classification
        detail: { types: classificationTypeIds }, // Array of type IDs, not labels
      })

      // Set classification and navigate to denial page
      setClassification(classificationData)

      toast.success('Visit created. Please provide denial reason.')
    } catch (error: any) {
      console.error('Failed to create visit:', error)
      toast.error(error.message || 'Failed to create visit. Please try again.')
      setIsCreatingVisit(false)
    }
  }

  // Helper function to toggle classification selection
  const toggleClassification = (classification: ClientClassification) => {
    const current = form.getValues('client_classifications')
    const updated = current.includes(classification)
      ? current.filter((c) => c !== classification)
      : [...current, classification]
    form.setValue('client_classifications', updated)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-3xl font-bold text-primary text-center underline">
        Client's Information
      </h2>

      {/* Client Info Display (Read-Only) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 bg-gray-50 rounded-lg">
        <div className="space-y-4">
          <InfoRow label="Name" value={`${client.firstname} ${client.middlename || ''} ${client.lastname}`.trim()} />
          <InfoRow label="Address" value={[client.street_name, client.barangay, client.city, client.province].filter(Boolean).join(", ") || "N/A"} />
          <InfoRow label="Contact No." value={client.contact_number || 'N/A'} />
          <InfoRow label="Email" value={client.email || 'N/A'} />
          <InfoRow label="Citizenship" value={client.citizenship || 'N/A'} />
          <InfoRow
            label="Monthly Income"
            value={client.monthly_income ? `₱${client.monthly_income.toLocaleString()}` : 'N/A'}
          />
        </div>

        <div className="space-y-4">
          <InfoRow label="Age" value={age !== null ? `${age} Years Old` : 'N/A'} />
          <InfoRow label="Gender" value={client.gender || 'N/A'} />
          <InfoRow label="Civil Status" value={client.civil_status || 'N/A'} />
        </div>
      </div>

      <Separator className="my-8" />

      {/* Classification Form */}
      <Form {...form}>
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-primary">Client Classifications</h3>

          {/* Client Classifications */}
          <FormField
            control={form.control}
            name="client_classifications"
            render={() => (
              <FormItem>
                <FormLabel className="text-base font-semibold">Client Classifications</FormLabel>
                <p className="text-sm text-muted-foreground mb-4">
                  Select all classifications that apply to this client (multiple selections allowed)
                </p>

                <div className="space-y-4">
                  {/* General Classifications */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold text-primary">
                        General Classifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {GENERAL_CLASSIFICATIONS.map((classification) => (
                        <div key={classification} className="flex items-center space-x-3">
                          <Checkbox
                            checked={form.watch('client_classifications').includes(classification)}
                            onCheckedChange={() => toggleClassification(classification)}
                            id={`general-${classification}`}
                          />
                          <label
                            htmlFor={`general-${classification}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {classification}
                          </label>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* PWD Classifications */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold text-primary">
                        PWD (Person with Disability) Classifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {PWD_CLASSIFICATIONS.map((classification) => (
                        <div key={classification} className="flex items-center space-x-3">
                          <Checkbox
                            checked={form.watch('client_classifications').includes(classification)}
                            onCheckedChange={() => toggleClassification(classification)}
                            id={`pwd-${classification}`}
                          />
                          <label
                            htmlFor={`pwd-${classification}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {classification}
                          </label>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Other Classifications */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold text-primary">
                        Other Classifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {OTHER_CLASSIFICATIONS.map((classification) => (
                        <div key={classification} className="flex items-center space-x-3">
                          <Checkbox
                            checked={form.watch('client_classifications').includes(classification)}
                            onCheckedChange={() => toggleClassification(classification)}
                            id={`other-${classification}`}
                          />
                          <label
                            htmlFor={`other-${classification}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {classification}
                          </label>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <FormMessage />
              </FormItem>
            )}
          />

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={isCreatingVisit}
            >
              Back
            </Button>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDenyDialog(true)}
                disabled={isCreatingVisit}
                className="bg-accent hover:bg-accent/90"
              >
                {isCreatingVisit ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Visit...
                  </>
                ) : (
                  'Deny Client'
                )}
              </Button>
              <Button
                type="button"
                onClick={() => setShowAssistDialog(true)}
                disabled={isCreatingVisit}
                className="bg-primary hover:bg-primary/90"
              >
                {isCreatingVisit ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Visit...
                  </>
                ) : (
                  'Assist Client'
                )}
              </Button>
            </div>
          </div>
        </div>
      </Form>

      {/* Assist Confirmation Dialog */}
      <AlertDialog open={showAssistDialog} onOpenChange={setShowAssistDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assist Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to assist this client? They will proceed to select assistance types.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAssist}
              className="bg-primary hover:bg-primary/90"
            >
              Confirm Assist
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deny Confirmation Dialog */}
      <AlertDialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deny Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deny this client? You will need to provide a reason for denial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeny}
              className="bg-accent hover:bg-accent/90"
            >
              Confirm Deny
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Helper component for client info display
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center">
      <label className="w-40 font-bold text-sm">{label}:</label>
      <div className="flex-1 border-b border-gray-400 pb-1 text-sm">{value}</div>
    </div>
  )
}
